import { ImageMetadata } from '@/api/models';

export const PHOTO_PAGE_SIZE = 5;

export type WedgePhotoType = 'sole' | 'leadingEdge' | 'stamping';

export const WEDGE_PHOTO_SORT_IDS: Record<WedgePhotoType, number> = {
  sole: 1,
  leadingEdge: 2,
  stamping: 3,
};

export interface IPhotoLike {
  id: string | number;
  createdAt: number;
  type?: WedgePhotoType;
  isPlaceholder?: boolean;
}

export interface ICaptionPhotoLike {
  id: string | number;
  caption: string;
  label?: string;
}

export interface ILocalPhoto extends IPhotoLike, ICaptionPhotoLike {
  src: string;
  name: string;
  size: number;
  alt?: string;
  typeLabel?: string;
  uploadStatus?: 'local' | 'processing' | 'complete' | 'error';
  uploadError?: string;
  fileName?: string;
  contentType?: string;
  storageObjectId?: number;
  entityGuid?: string;
  metadataRowGuid?: string;
  sortId?: number;
  sourceBlob?: Blob;
}

interface ICreateLocalPhotoProps {
  photo: Blob;
  index: number;
  uploadTime: number;
  src: string;
  type?: WedgePhotoType;
}

export interface IWedgePhotoSlot<TPhoto> {
  type: WedgePhotoType;
  label: string;
  photo?: TPhoto;
}

export const wedgePhotoSlots: IWedgePhotoSlot<never>[] = [
  { type: 'sole', label: 'Sole' },
  { type: 'leadingEdge', label: 'Leading Edge' },
  { type: 'stamping', label: 'Stamping' },
];

export const getWedgePhotoTypeLabel = (type: WedgePhotoType) => {
  const slot = wedgePhotoSlots.find((wedgePhotoSlot) => wedgePhotoSlot.type === type);
  return slot?.label ?? '';
};

export const getWedgePhotoSortId = (type?: WedgePhotoType) => {
  if (!type) return undefined;
  return WEDGE_PHOTO_SORT_IDS[type];
};

export const getWedgePhotoTypeFromSortId = (sortId?: number | null): WedgePhotoType | undefined => {
  const entry = Object.entries(WEDGE_PHOTO_SORT_IDS).find(([, wedgeSortId]) => wedgeSortId === sortId);
  return entry?.[0] as WedgePhotoType | undefined;
};

export const createPhotoId = () => {
  if (globalThis.crypto?.randomUUID) {
    return globalThis.crypto.randomUUID();
  }

  return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
};

export const getPhotoName = (photo: Blob, index: number) => {
  if (typeof File !== 'undefined' && photo instanceof File && photo.name) {
    return photo.name;
  }

  return `Photo ${index + 1}`;
};

export const createLocalPhoto = ({ photo, index, uploadTime, src, type }: ICreateLocalPhotoProps): ILocalPhoto => {
  const name = getPhotoName(photo, index);
  const typeLabel = type ? getWedgePhotoTypeLabel(type) : undefined;

  return {
    id: createPhotoId(),
    src,
    caption: name,
    createdAt: uploadTime,
    name,
    size: photo.size,
    label: name,
    alt: name,
    type,
    typeLabel,
    uploadStatus: 'local',
    sourceBlob: photo,
  };
};

export const createPhotoFromMetadata = (metadata: ImageMetadata, src: string): ILocalPhoto => {
  const caption = metadata.caption || metadata.fileName;
  const createdAt = Date.parse(metadata.createDate) || 0;

  return {
    id: metadata.storageObjectId,
    src,
    caption,
    createdAt,
    name: metadata.fileName,
    size: metadata.size ?? 0,
    label: caption,
    alt: caption,
    uploadStatus: 'complete',
    fileName: metadata.fileName,
    contentType: metadata.contentType ?? undefined,
    storageObjectId: metadata.storageObjectId,
    entityGuid: metadata.entityGuid,
    metadataRowGuid: metadata.rowGuid,
    sortId: metadata.sortId,
  };
};

export const revokePhotoUrl = (photoUrls: string[], src: string) => {
  URL.revokeObjectURL(src);
  const photoUrlIndex = photoUrls.indexOf(src);

  if (photoUrlIndex >= 0) {
    photoUrls.splice(photoUrlIndex, 1);
  }
};

export const revokePhotoUrls = (photoUrls: string[]) => {
  photoUrls.forEach((photoUrl) => URL.revokeObjectURL(photoUrl));
  photoUrls.splice(0, photoUrls.length);
};

export const sortRegularPhotosNewestFirst = <TPhoto extends IPhotoLike>(photos: TPhoto[]) => {
  return [...photos].sort((photoA, photoB) => photoB.createdAt - photoA.createdAt);
};

const isSamePersistedPhoto = <TPhoto extends Pick<ILocalPhoto, 'fileName' | 'storageObjectId'>>(photoA: TPhoto, photoB: TPhoto) => {
  if (photoA.storageObjectId && photoA.storageObjectId === photoB.storageObjectId) {
    return true;
  }

  return Boolean(photoA.fileName && photoB.fileName && photoA.fileName === photoB.fileName);
};

export const mergeHydratedPhotosWithLocal = <TPhoto extends ILocalPhoto>(hydratedPhotos: TPhoto[], localPhotos: TPhoto[]) => {
  const dedupedHydratedPhotos = hydratedPhotos.filter((hydratedPhoto) => !localPhotos.some((localPhoto) => isSamePersistedPhoto(hydratedPhoto, localPhoto)));
  return [...dedupedHydratedPhotos, ...localPhotos];
};

export const getVisiblePhotoItems = <TPhoto>(photos: TPhoto[], pageIndex: number, pageSize = PHOTO_PAGE_SIZE) => {
  const startIndex = pageIndex * pageSize;
  return photos.slice(startIndex, startIndex + pageSize);
};

export const getMaxPhotoPageIndex = (totalItems: number, pageSize = PHOTO_PAGE_SIZE) => {
  return Math.max(Math.ceil(totalItems / pageSize) - 1, 0);
};

export const getNextPhotoPageIndex = (totalItems: number, pageIndex: number, pageSize = PHOTO_PAGE_SIZE) => {
  return Math.min(pageIndex + 1, getMaxPhotoPageIndex(totalItems, pageSize));
};

export const getPreviousPhotoPageIndex = (pageIndex: number) => {
  return Math.max(pageIndex - 1, 0);
};

export const getPhotoPageIndexAfterAdd = (pageIndex: number) => {
  return pageIndex;
};

export const editPhotoCaption = <TPhoto extends ICaptionPhotoLike>(photos: TPhoto[], photoId: TPhoto['id'], caption: string) => {
  return photos.map((photo) => {
    if (photo.id !== photoId) return photo;
    return { ...photo, caption, label: caption };
  });
};

export const deletePhoto = <TPhoto extends { id: string | number }>(photos: TPhoto[], photoId: TPhoto['id'], activePhotoId?: TPhoto['id']) => {
  const deletedPhotoIndex = photos.findIndex((photo) => photo.id === photoId);
  const nextPhotos = photos.filter((photo) => photo.id !== photoId);

  if (!nextPhotos.length) {
    return { photos: nextPhotos, activePhotoId: undefined };
  }

  if (activePhotoId !== photoId) {
    return { photos: nextPhotos, activePhotoId };
  }

  const nextActivePhoto = nextPhotos[Math.min(Math.max(deletedPhotoIndex, 0), nextPhotos.length - 1)];
  return { photos: nextPhotos, activePhotoId: nextActivePhoto.id };
};

export const buildWedgePhotoItems = <TPhoto extends IPhotoLike>(typedPhotos: Partial<Record<WedgePhotoType, TPhoto>>, regularPhotos: TPhoto[]) => {
  const typedItems = wedgePhotoSlots.map((slot) => typedPhotos[slot.type] ?? ({ id: slot.type, type: slot.type, isPlaceholder: true, createdAt: 0 } as TPhoto));
  return [...typedItems, ...regularPhotos];
};

export const replaceWedgeTypedPhoto = <TPhoto extends IPhotoLike>(typedPhotos: Partial<Record<WedgePhotoType, TPhoto>>, type: WedgePhotoType, photo: TPhoto) => {
  return { ...typedPhotos, [type]: { ...photo, type, typeLabel: getWedgePhotoTypeLabel(type) } };
};
